import { apiRequest } from '../lib/api-client';
import type { FormView } from './api';

/** The form builder and its results (context.md sections 74-79). */

export const FIELD_TYPES = [
  'TEXT',
  'TEXTAREA',
  'EMAIL',
  'PHONE',
  'NUMBER',
  'DATE',
  'SELECT',
  'MULTISELECT',
  'RADIO',
  'CHECKBOX',
  'BOOLEAN',
  'RATING',
  'NPS_SCORE',
  'HIDDEN',
  'FILE',
  'CONSENT',
] as const;

export type FieldType = (typeof FIELD_TYPES)[number];

/** The types whose answers come from a list the author writes. */
export const CHOICE_TYPES: FieldType[] = ['SELECT', 'MULTISELECT', 'RADIO', 'CHECKBOX'];

export interface FieldRow {
  id?: string;
  key: string;
  label: string;
  help_text?: string | null;
  placeholder?: string | null;
  field_type: FieldType;
  required: boolean;
  position: number;
  options?: { value: string; label?: string }[] | null;
  maps_to?: string | null;
}

export interface ActionRow {
  id?: string;
  action_type: string;
  config: Record<string, unknown>;
}

export interface FormDetail extends FormView {
  fields: FieldRow[];
  actions: ActionRow[];
  publicUrl?: string;
  successMessage?: string | null;
  redirectUrl?: string | null;
  singleResponse?: boolean;
  version?: number;
}

export function getForm(id: string): Promise<FormDetail> {
  return apiRequest(`/v1/forms/${id}`);
}

export interface FieldInput {
  key: string;
  label: string;
  fieldType: FieldType;
  required?: boolean;
  position?: number;
  helpText?: string | null;
  placeholder?: string | null;
  options?: { value: string; label?: string }[];
  mapsTo?: string | null;
}

export interface FormInput {
  name?: string;
  description?: string | null;
  successMessage?: string | null;
  singleResponse?: boolean;
  fields?: FieldInput[];
  expectedVersion?: number;
}

export function createForm(input: {
  slug: string;
  name: string;
  kind?: string;
  fields?: FieldInput[];
}): Promise<FormDetail> {
  return apiRequest('/v1/forms', { method: 'POST', body: input });
}

export function updateForm(id: string, input: FormInput): Promise<FormDetail> {
  return apiRequest(`/v1/forms/${id}`, { method: 'PATCH', body: input });
}

export function publishForm(id: string): Promise<FormDetail> {
  return apiRequest(`/v1/forms/${id}/publish`, { method: 'POST' });
}

export function unpublishForm(id: string): Promise<FormDetail> {
  return apiRequest(`/v1/forms/${id}/unpublish`, { method: 'POST' });
}

export interface SubmissionRow {
  id: string;
  data: Record<string, unknown>;
  score: number | null;
  score_band: string | null;
  status: 'RECEIVED' | 'PROCESSED' | 'FAILED' | 'SPAM';
  utm: Record<string, string>;
  referrer: string | null;
  lead_id: string | null;
  contact_id: string | null;
  failure_reason: string | null;
  created_at: string;
}

export function listSubmissions(
  id: string,
  cursor?: string,
): Promise<{ submissions: SubmissionRow[]; nextCursor: string | null }> {
  const params = new URLSearchParams({ limit: '50' });

  if (cursor) params.set('cursor', cursor);

  return apiRequest(`/v1/forms/${id}/submissions?${params.toString()}`);
}

/**
 * A field key the API will accept: `^[a-z][a-z0-9_]{0,62}$`.
 *
 * Derived from the label rather than asked for, because it is the part authors get wrong and the
 * part that is hardest to change later — the key is what an answer is stored under and what an
 * automation reads.
 */
export function keyFrom(label: string): string {
  const key = label
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 63);

  // A key must start with a letter; a label that is only digits still needs one.
  return /^[a-z]/.test(key) ? key : `campo_${key}`.slice(0, 63);
}
